import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { LogIn, Eye, EyeOff, Store, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const MerchantLoginPage = () => {
  const navigate = useNavigate();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!login.trim() || !password.trim()) {
      toast.error('Заполните все поля');
      return;
    }

    try {
      setLoading(true);
      
      const response = await api.post('/auth/merchant-login', {
        login: login.trim(),
        password: password.trim(),
      });
      
      const { access_token, merchant_name, merchant_id } = response.data;
      
      // Store merchant token
      localStorage.setItem('impersonate_token', access_token);
      localStorage.setItem('impersonate_merchant', JSON.stringify({
        name: merchant_name,
        id: merchant_id,
      }));
      
      toast.success(`Добро пожаловать, ${merchant_name}!`);
      navigate('/cabinet');
    } catch (err) {
      const message = err.response?.data?.detail || 'Ошибка входа';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to admin login */}
        <Button
          variant="ghost"
          onClick={() => navigate('/login')}
          className="mb-6 text-slate-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Вход для администраторов
        </Button>
        
        <Card className="bg-slate-800/50 border-slate-700/50 backdrop-blur">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center">
              <Store className="w-8 h-8 text-emerald-500" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold text-white">
                Личный кабинет мерчанта
              </CardTitle>
              <CardDescription className="text-slate-400 mt-2">
                Войдите с данными, которые вам предоставил менеджер
              </CardDescription>
            </div>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login" className="text-slate-300">
                  Логин
                </Label>
                <Input
                  id="login"
                  type="text"
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  placeholder="Ваш логин"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                  disabled={loading}
                  autoComplete="username"
                  data-testid="merchant-login-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">
                  Пароль
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Ваш пароль"
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 pr-10"
                    disabled={loading}
                    autoComplete="current-password"
                    data-testid="merchant-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 text-slate-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                disabled={loading}
                data-testid="merchant-login-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Вход...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="w-4 h-4" />
                    Войти
                  </span>
                )}
              </Button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-slate-700">
              <p className="text-sm text-slate-500 text-center">
                Нет данных для входа?{' '}
                <a 
                  href="mailto:support@oxypay.kz" 
                  className="text-emerald-400 hover:text-emerald-300"
                >
                  Свяжитесь с поддержкой
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
        
        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-6">
          © {new Date().getFullYear()} OxyPay. Все права защищены.
        </p>
      </div>
    </div>
  );
};

export default MerchantLoginPage;
